/**
 * Requests Tab Component
 *
 * Main component for managing access requests with sub-tabs for pending, approved, and denied requests
 */

import React, { useState, useEffect } from 'react';
import type { AccessRequest } from '../../../types/access-control';
import { AccessRequestCard } from './AccessRequestCard';
import { ApproveRequestModal } from './ApproveRequestModal';
import { DenyRequestModal } from './DenyRequestModal';
import {
  getAccessRequests,
  approveAccessRequest,
  denyAccessRequest,
  deleteAccessRequest,
} from '../../../services/api/access-control';

type RequestStatus = 'pending' | 'approved' | 'denied';

export const RequestsTab: React.FC = () => {
  const [status, setStatus] = useState<RequestStatus>('pending');
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [approvingRequest, setApprovingRequest] = useState<AccessRequest | null>(null);
  const [denyingRequest, setDenyingRequest] = useState<AccessRequest | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchRequests = async () => {
    try {
      setError(null);
      const data = await getAccessRequests(status);
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const data = await getAccessRequests('pending');
      setPendingCount(data.length);
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchRequests();

    // Fetch pending count for badge
    if (status !== 'pending') {
      fetchPendingCount();
    }

    // Auto-refresh pending requests every 30 seconds
    if (status === 'pending') {
      const interval = setInterval(() => {
        fetchRequests();
      }, 30000);
      return () => clearInterval(interval);
    }

    return undefined;
  }, [status]);

  const handleApprove = (request: AccessRequest) => {
    setApprovingRequest(request);
  };

  const handleDeny = (request: AccessRequest) => {
    setDenyingRequest(request);
  };

  const handleApproveConfirm = async (reviewNotes: string, addToWhitelist: boolean) => {
    if (!approvingRequest) return;

    try {
      await approveAccessRequest(approvingRequest.id, reviewNotes, addToWhitelist);
      setSuccessMessage(`Access request from ${approvingRequest.email} has been approved`);
      setApprovingRequest(null);

      // Refresh the list
      await fetchRequests();

      // Update pending count
      if (status !== 'pending') {
        await fetchPendingCount();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      throw err; // Let the modal handle the error display
    }
  };

  const handleDenyConfirm = async (reviewNotes: string) => {
    if (!denyingRequest) return;

    try {
      await denyAccessRequest(denyingRequest.id, reviewNotes);
      setSuccessMessage(`Access request from ${denyingRequest.email} has been denied`);
      setDenyingRequest(null);

      // Refresh the list
      await fetchRequests();

      // Update pending count
      if (status !== 'pending') {
        await fetchPendingCount();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      throw err; // Let the modal handle the error display
    }
  };

  const handleDelete = async (request: AccessRequest) => {
    if (!confirm(`Are you sure you want to delete the access request from ${request.email}?`)) {
      return;
    }

    try {
      await deleteAccessRequest(request.id);
      setSuccessMessage(`Access request from ${request.email} has been deleted`);

      // Refresh the list
      await fetchRequests();

      // Update pending count if we're not on pending tab
      if (status !== 'pending') {
        await fetchPendingCount();
      }

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete request';
      setError(errorMsg);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    fetchRequests();
  };

  return (
    <div className="space-y-4">
      {/* Sub-tab navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setStatus('pending')}
          className={`px-4 py-2 font-semibold text-sm transition-colors ${
            status === 'pending'
              ? 'border-b-2 border-yellow-600 text-yellow-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Pending {status !== 'pending' && pendingCount > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
              {pendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setStatus('approved')}
          className={`px-4 py-2 font-semibold text-sm transition-colors ${
            status === 'approved'
              ? 'border-b-2 border-green-600 text-green-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Approved
        </button>
        <button
          onClick={() => setStatus('denied')}
          className={`px-4 py-2 font-semibold text-sm transition-colors ${
            status === 'denied'
              ? 'border-b-2 border-red-600 text-red-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Denied
        </button>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setSuccessMessage(null)}
                className="inline-flex text-green-400 hover:text-green-600"
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-gray-600">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm font-medium">Loading requests...</span>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button
              onClick={handleRetry}
              className="ml-3 px-3 py-1 text-xs font-semibold bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Requests list */}
      {!loading && !error && (
        <>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No requests</h3>
              <p className="mt-1 text-sm text-gray-500">
                {status === 'pending' && 'No pending access requests at this time.'}
                {status === 'approved' && 'No approved access requests yet.'}
                {status === 'denied' && 'No denied access requests yet.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => {
                const props: any = {
                  key: request.id,
                  request,
                  onDelete: handleDelete,
                };
                if (status === 'pending') {
                  props.onApprove = handleApprove;
                  props.onDeny = handleDeny;
                }
                return <AccessRequestCard {...props} />;
              })}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {approvingRequest && (
        <ApproveRequestModal
          request={approvingRequest}
          onConfirm={handleApproveConfirm}
          onCancel={() => setApprovingRequest(null)}
        />
      )}

      {denyingRequest && (
        <DenyRequestModal
          request={denyingRequest}
          onConfirm={handleDenyConfirm}
          onCancel={() => setDenyingRequest(null)}
        />
      )}
    </div>
  );
};
